import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { baseUrl } from "../../api/apiSlice";

export type PdfExportProgressStage =
    | "preparing"
    | "photos"
    | "rendering"
    | "saving";

export interface PdfExportProgress {
    stage: PdfExportProgressStage;
    message: string;
    current?: number;
    total?: number;
}

/** Letter width at 96dpi — matches the on-screen export preview width. */
const PAGE_WIDTH_PX = 816;
const MARGIN_PT = 28;
const LETTER_WIDTH_PT = 612;
const LETTER_HEIGHT_PT = 792;
const CONTENT_WIDTH_PT = LETTER_WIDTH_PT - MARGIN_PT * 2;
const CONTENT_HEIGHT_PT = LETTER_HEIGHT_PT - MARGIN_PT * 2;
/** CSS page height matched to PDF content box aspect ratio. */
const PAGE_HEIGHT_PX = Math.round(
    PAGE_WIDTH_PX * (CONTENT_HEIGHT_PT / CONTENT_WIDTH_PT)
);
const PAGE_PADDING_PX = 20;

async function waitForImages(root: HTMLElement): Promise<void> {
    const images = Array.from(root.querySelectorAll("img"));
    await Promise.all(
        images.map(
            (img) =>
                new Promise<void>((resolve) => {
                    if (img.complete && img.naturalWidth > 0) {
                        resolve();
                        return;
                    }
                    const done = () => resolve();
                    img.addEventListener("load", done, { once: true });
                    img.addEventListener("error", done, { once: true });
                })
        )
    );
}

/**
 * Rewrites Azure blob <img> sources to same-origin blob URLs via the API proxy,
 * avoiding browser CORS blocks during html2canvas export.
 */
async function hydrateImagesViaApi(
    root: HTMLElement,
    inspectionId: number,
    token: string,
    onProgress?: (progress: PdfExportProgress) => void
): Promise<() => void> {
    const images = Array.from(
        root.querySelectorAll<HTMLImageElement>("img[data-pti-item-id]")
    ).filter((img) => Boolean(img.dataset.ptiItemId));

    const objectUrls: string[] = [];
    const total = images.length;

    for (let index = 0; index < images.length; index++) {
        const img = images[index];
        const itemId = Number(img.dataset.ptiItemId);
        if (!Number.isFinite(itemId) || itemId <= 0) {
            continue;
        }

        onProgress?.({
            stage: "photos",
            message: `Preparing photos (${index + 1}/${total})…`,
            current: index + 1,
            total,
        });

        try {
            const response = await fetch(
                `${baseUrl}/pti/${inspectionId}/items/${itemId}/photo`,
                {
                    method: "GET",
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (!response.ok) {
                continue;
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            objectUrls.push(objectUrl);
            img.removeAttribute("crossorigin");
            img.src = objectUrl;
        } catch {
            // Keep original src; photo may be missing in the PDF.
        }
    }

    await waitForImages(root);

    return () => {
        objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
}

export interface DownloadPtiDocumentPdfOptions {
    element: HTMLElement;
    fileName: string;
    inspectionId: number;
    token: string;
    onProgress?: (progress: PdfExportProgress) => void;
}

let ptiPdfDownloadInFlight = false;

export function isPtiPdfDownloadInFlight(): boolean {
    return ptiPdfDownloadInFlight;
}

/**
 * Clones the preview into a body-mounted host so PDF export keeps working
 * even if the user navigates away from the PTI page (which unmounts the source).
 */
function mountDetachedExportRoot(source: HTMLElement): {
    root: HTMLElement;
    dispose: () => void;
} {
    const host = document.createElement("div");
    host.setAttribute("data-pti-pdf-export-host", "true");
    host.setAttribute("aria-hidden", "true");
    Object.assign(host.style, {
        position: "fixed",
        left: "-10000px",
        top: "0",
        width: `${PAGE_WIDTH_PX}px`,
        pointerEvents: "none",
        zIndex: "-1",
        backgroundColor: "#ffffff",
    });

    const clone = source.cloneNode(true) as HTMLElement;
    clone.removeAttribute("id");
    clone.style.padding = "0";
    clone.style.margin = "0";
    clone.style.width = `${PAGE_WIDTH_PX}px`;
    clone.style.setProperty("--pti-pdf-page-height", `${PAGE_HEIGHT_PX}px`);
    clone.style.setProperty("--pti-pdf-page-width", `${PAGE_WIDTH_PX}px`);
    host.appendChild(clone);
    document.body.appendChild(host);

    return {
        root: clone,
        dispose: () => {
            host.remove();
        },
    };
}

function createPdfPage(): HTMLElement {
    const page = document.createElement("div");
    page.setAttribute("data-pti-pdf-page", "true");
    Object.assign(page.style, {
        width: `${PAGE_WIDTH_PX}px`,
        height: `${PAGE_HEIGHT_PX}px`,
        boxSizing: "border-box",
        padding: `${PAGE_PADDING_PX}px`,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        color: "#111111",
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    });
    return page;
}

/**
 * Moves each [data-pti-pdf-page-block] into fixed-height Letter pages.
 * A block never starts on a page unless the whole block fits (unless it alone
 * is taller than one page — then it is clipped rather than split).
 */
function packBlocksIntoPages(root: HTMLElement): HTMLElement[] {
    const blocks = Array.from(
        root.querySelectorAll<HTMLElement>("[data-pti-pdf-page-block]")
    );
    blocks.forEach((block) => block.remove());

    const stack = document.createElement("div");
    root.replaceChildren(stack);

    const pages: HTMLElement[] = [];
    let page = createPdfPage();
    stack.appendChild(page);
    pages.push(page);

    for (const block of blocks) {
        page.appendChild(block);
        // Force layout after insert.
        void page.offsetHeight;

        const overflows = page.scrollHeight > page.clientHeight + 1;
        if (!overflows) {
            continue;
        }

        // Only block on the page and still too tall: leave it (overflow hidden).
        if (page.childElementCount === 1) {
            continue;
        }

        // Move this block to a fresh page so the previous page stays intact.
        page.removeChild(block);
        page = createPdfPage();
        stack.appendChild(page);
        pages.push(page);
        page.appendChild(block);
        void page.offsetHeight;
    }

    return pages;
}

/**
 * Renders a DOM node (HTML document preview) to a multi-page Letter PDF and downloads it.
 * Each PDF page is a full fixed-height capture — content is never sliced mid-block.
 */
export async function downloadElementAsPdf(
    options: DownloadPtiDocumentPdfOptions
): Promise<void> {
    if (ptiPdfDownloadInFlight) {
        throw new Error("A PTI PDF download is already in progress.");
    }

    const { element, fileName, inspectionId, token, onProgress } = options;
    ptiPdfDownloadInFlight = true;

    onProgress?.({
        stage: "preparing",
        message: "Preparing document…",
    });

    const { root, dispose: disposeHost } = mountDetachedExportRoot(element);
    let cleanupImages: (() => void) | undefined;

    try {
        cleanupImages = await hydrateImagesViaApi(
            root,
            inspectionId,
            token,
            onProgress
        );

        onProgress?.({
            stage: "rendering",
            message: "Laying out pages…",
        });

        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });

        const pages = packBlocksIntoPages(root);

        onProgress?.({
            stage: "rendering",
            message: "Rendering pages…",
        });

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "letter",
        });

        for (let index = 0; index < pages.length; index++) {
            onProgress?.({
                stage: "rendering",
                message: `Rendering page ${index + 1} of ${pages.length}…`,
                current: index + 1,
                total: pages.length,
            });

            const page = pages[index];
            const canvas = await html2canvas(page, {
                scale: 2,
                useCORS: true,
                allowTaint: false,
                backgroundColor: "#ffffff",
                logging: false,
                scrollX: 0,
                scrollY: 0,
                width: PAGE_WIDTH_PX,
                height: PAGE_HEIGHT_PX,
                windowWidth: PAGE_WIDTH_PX,
                windowHeight: PAGE_HEIGHT_PX,
            });

            if (!canvas.width || !canvas.height) {
                throw new Error("PDF capture produced an empty page. Please try again.");
            }

            if (index > 0) {
                pdf.addPage();
            }

            const imageData = canvas.toDataURL("image/jpeg", 0.92);
            pdf.addImage(
                imageData,
                "JPEG",
                MARGIN_PT,
                MARGIN_PT,
                CONTENT_WIDTH_PT,
                CONTENT_HEIGHT_PT
            );
        }

        onProgress?.({
            stage: "saving",
            message: "Saving PDF…",
        });

        pdf.save(fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`);
    } finally {
        cleanupImages?.();
        disposeHost();
        ptiPdfDownloadInFlight = false;
    }
}
